"use client";

/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable jsx-a11y/media-has-caption */
import SharedComponents, { Avatar, Modal } from "@audacia-hq/shared/components";
import { useLayout } from "@audacia-hq/shared/contexts";
import { shuffleArray, unurlifyPseudo } from "@audacia-hq/shared/utils";
import {
  faEnvelope,
  faHeart as faRegHeart,
  faCirclePlay as faRegPlay,
  faCircleStop as faRegStop,
} from "@fortawesome/free-regular-svg-icons";
import {
  faAngleLeft,
  faArrowRightLong,
  faCircleNotch,
  faPeopleArrows,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tag } from "antd";
import Link from "next/link";
import { useRouter } from "next/navigation";
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import MemberAlertModal from "../../../components/alerts/MemberAlertModal";
import Button from "../../../components/Button";
import ExpertAvailabilitlyBadge from "../../../components/experts/ExpertAvailabilityBadge";
import ExpertBusyDuration from "../../../components/experts/ExpertBusyDuration";
import ExpertButton from "../../../components/experts/ExpertButton";
import ExpertDetailCard from "../../../components/experts/ExpertDetailCard";
import ExpertPlanning from "../../../components/experts/ExpertPlanning";
import ExpertQueueBadge from "../../../components/experts/ExpertQueueBadge";
import PrivateMessageModal from "../../../components/experts/PrivateMessageModal";
import RatingsList from "../../../components/experts/RatingsList";
import TextEllipsis, { BtnProps } from "../../../components/TextEllipsis";
import { useExpert } from "../../../context/ExpertContext";
import { useLocale } from "../../../context/LocaleContext";
import { useMember } from "../../../context/MemberContext";
import { GET_EXPERT_CONTENT, GET_EXPERT_LIMITS } from "../../../graphql/query";
import { useLazyFetcher } from "../../../hooks/useClientFetcher";
import { Expert, ExpertContent, ExpertIdentifier } from "../../../models/expert";
import { MemberAlert } from "../../../models/member";
import { getRatings, getRatingsSummaryList } from "../../../services/api";
import PageNotFound from "../../404";

import { RatingList, RatingSummary } from "@/models/ratings";

const ellipsisBtnFactory = (label: string, className?: string) => {
  const fc: React.FC<BtnProps> = ({ toggle }) => (
    <Button onClick={toggle} className={className || ""} size="small" render="simpleGray">{label}</Button>
  );
  return fc;
};

interface Props {
  expertIdentifier: string;
}

const ExpertDetail: React.FC<Props> = ({ expertIdentifier }) => {
  const { t } = useTranslation("expert");
  const router = useRouter();
  const language = useLocale();

  const {
    member, addMemberFavorite, removeMemberFavorite, setModalSignup,
  } = useMember();
  const {
    getExperts, expertIdentifiers, expertUpdateFeed,
  } = useExpert();

  const [getExpertContent, {
    data, loading: contentLoading,
  }] = useLazyFetcher<{ expert: { content: ExpertContent } }>(GET_EXPERT_CONTENT);

  const [loading, setLoading] = useState(true);
  const [expertUid, setExpertUid] = useState<string>();
  const [expert, setExpert] = useState<Expert>();

  const [getExpertLimits, { data: expertLimits }] = useLazyFetcher<{
    expertLimits?: { consultationSecondCtr: number, availableMessageNbr: number }
  }>(GET_EXPERT_LIMITS);

  const [defaultRatings, setDefaultRatings] = useState<RatingList>();
  const [ratingSummaryList, setRatingsSummary] = useState<RatingSummary[]>();

  const [modal, setModal] = useState<boolean>(false);
  const [showMemberAlertModal, setShowMemberAlertModal] = useState<boolean>(false);
  const [showPMExplanationModal, setShowPMExplanationModal] = useState<boolean>(false);

  const [suggestedUids, setSuggestedUids] = useState<string[]>([]);
  const [suggestedExperts, setSuggestedExperts] = useState<Expert[]>();

  const { mainHeight } = useLayout();
  const profileRef = useRef<HTMLDivElement>(null);
  const consultationRef = useRef<HTMLDivElement>(null);
  const [profileHeight, setProfileHeight] = useState<number>();
  const [consultationHeight, setConsultationHeight] = useState<number>();
  const [memberAlert, setMemberAlert] = useState<MemberAlert>();
  const [showLegend, setShowLegend] = useState<boolean>(false);

  useEffect(() => {
    if (!consultationRef.current) return;
    const resizeObserver = new ResizeObserver((entries) => {
      if (!consultationRef.current) return;
      setConsultationHeight(consultationRef.current?.clientHeight);
    });
    resizeObserver.observe(document.body);

    const mutationObserver = new MutationObserver((entries) => {
      setConsultationHeight(consultationRef?.current?.clientHeight);
    });
    // eslint-disable-next-line consistent-return
    return () => {
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
    };
  }, [consultationRef.current]);

  useEffect(() => {
    if (!profileRef.current) return;
    const resizeObserver = new ResizeObserver((entries) => {
      if (!profileRef.current) return;
      setProfileHeight(profileRef.current.clientHeight);
    });
    resizeObserver.observe(document.body);

    const mutationObserver = new MutationObserver((entries) => {
      setProfileHeight(profileRef.current?.clientHeight);
    });
    // eslint-disable-next-line consistent-return
    return () => {
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
    };
  }, [profileRef.current]);

  const [vocalMessagePlaying, setVocalMessagePlaying] = useState<boolean>(false);
  const vocalMessageElementRef = useRef<HTMLAudioElement>(null);

  const nbSuggestions = 6;

  useEffect(() => {
    const eUid = Object.values(expertIdentifiers)?.find(
      (e) => e.expertUid === expertIdentifier || e.pseudo === unurlifyPseudo(expertIdentifier),
    )?.expertUid;
    if (!eUid) setLoading(false);
    else {
      setExpertUid(eUid);
      getExpertLimits({ variables: { expertUid: eUid } });
    }
  }, [expertIdentifier]);

  const findOnlines = (
    list: ExpertIdentifier[],
    amount: number,
    exclude: string[] = [],
  ) => shuffleArray(list.filter(({ expertUid, online, hidden }) => online && !hidden && !exclude.includes(expertUid)))
    .slice(0, amount)
    .map((e) => e.expertUid);

  useEffect(() => {
    if (!expertUid) return;
    setSuggestedUids(findOnlines(Object.values(expertIdentifiers), nbSuggestions, [expertUid]));
  }, [expertUid]);

  useEffect(() => {
    if (!expertUid) return;
    if (suggestedUids.length > 0) {
      getExperts([expertUid, ...suggestedUids]).then(([mainExpert, ...suggestions]) => {
        setExpert(mainExpert);
        setSuggestedExperts(suggestions);
        setLoading(false);
      });
    } else {
      getExperts([expertUid]).then(([mainExpert]) => {
        setExpert(mainExpert);
        setLoading(false);
      });
    }
    getRatings({
      context: "idealvoyance",
      offset: 0,
      limit: 10,
      language: language.toUpperCase(),
      expertUid,
    }).then((res) => setDefaultRatings(res.data.ratings));
    getRatingsSummaryList({
      context: "idealvoyance",
      language: language.toUpperCase(),
      expertUid,
    }).then((res) => setRatingsSummary(res.data.ratingsSummary));
  }, [expertUid, suggestedUids]);

  useEffect(() => {
    if (!expertUid) return;
    const sub = expertUpdateFeed.subscribe((expertUids) => {
      const mainUpdated = expertUids.includes(expertUid);
      if (!mainUpdated && !expertUids.some((uid) => suggestedUids.includes(uid))) return;
      const onlines = suggestedUids.filter((uid) => expertIdentifiers[uid]?.online && !expertIdentifiers[uid].hidden);
      if (onlines.length < nbSuggestions) {
        setSuggestedUids([
          ...onlines,
          ...findOnlines(Object.values(expertIdentifiers), nbSuggestions - onlines.length, [expertUid, ...onlines]),
        ]);
      } else { // only re-fetch main if no suggestedUids changes (otherwise it will be refetched alongside them)
        getExperts([expertUid]).then(([mainExpert]) => setExpert(mainExpert));
      }
    });
    // eslint-disable-next-line consistent-return
    return () => sub.unsubscribe();
  }, [expertUpdateFeed, getExperts, expertIdentifiers, expertUid]);

  useEffect(() => {
    if (expert) {
      getExpertContent({
        variables: {
          expertUid: expert?.expertUid,
          profileLanguage: language.toUpperCase(),
        },
      });
    }
  }, [expert]);

  useEffect(() => {
    setMemberAlert(member?.alerts?.find(({ expertUid }) => expertUid === expert?.expertUid));
  }, [expert?.expertUid, member?.alerts]);

  const isMemberFavorite = (member?.profile.favorites || []).findIndex(
    (favorite) => favorite === expert?.expertUid,
  ) !== -1;

  const handleAddMemberFavorite = () => {
    if (!expert?.expertUid) {
      return;
    }
    addMemberFavorite(expert?.expertUid);
  };

  const handleRemoveMemberFavorite = () => {
    if (!expert?.expertUid) {
      return;
    }
    removeMemberFavorite(expert?.expertUid);
  };

  const toggleVocalMessage = () => {
    if (vocalMessagePlaying) {
      setVocalMessagePlaying(false);
      vocalMessageElementRef.current?.pause();
      // TODO: Test if works well
      // vocalMessageElementRef.current?.currentTime = 0;
    } else {
      setVocalMessagePlaying(true);
      vocalMessageElementRef.current?.play();
    }
  };

  const handleAlertModal = () => {
    if (!member) {
      setModalSignup(true);
      return;
    }
    setShowMemberAlertModal(true);
  };

  const goToConsultation = () => (member ? router.push(`/my-consultations?search=${expert?.pseudo}&expertUid=${expert?.expertUid}`) : setModalSignup(true));

  return expert ? (
    <div className="relative">
      <div className="wrapper hidden sm:block p-9 md:px-6 pb-0">
        <button onClick={() => router.back()} type="button" className="flex gap-1 items-center">
          <FontAwesomeIcon
            className="text-2xl fa-solid fa-fw -ml-2"
            icon={faAngleLeft}
          />
          <span className="">{t("common:backToList")}</span>
        </button>
      </div>

      <div className="wrapper grid gap-x-12 md:gap-x-24 sm:grid-cols-2 md:grid-cols-3 sm:p-9 md:px-6 md:py-12">
        <div className="relative">
          <Link href="/" className="sm:hidden absolute top-8 left-4">
            <FontAwesomeIcon
              className="text-white text-3xl drop-shadow-sm fa-solid fa-fw"
              icon={faAngleLeft}
            />
          </Link>

          <Avatar
            name={expert.pseudo}
            uid={expert.expertUid}
            src={expert.picture}
            loading="lazy"
            className="hidden sm:block w-full aspect-square rounded-2xl"
          />
        </div>
        <Avatar
          name={expert.pseudo}
          uid={expert.expertUid}
          src={expert.picture}
          loading="lazy"
          className="fixed -z-10 sm:hidden w-full"
          lighten
          style={{
            height: `${(mainHeight + 28 - ((profileHeight || 0) + (consultationHeight || 0)))}px`,
            objectFit: "cover",
          }}
        />

        <div
          className="rounded-t-2xl row-span-2 md:row-auto sm:!mt-0"
          style={{ marginTop: `${(mainHeight - ((profileHeight || 0) + (consultationHeight || 0)))}px` }}
        >
          <div
            className="rounded-t-2xl pb-6 bg-slate-50"
            ref={profileRef}
          >
            <div className="flex flew-wrap sm:block items-center justify-between py-3 pl-3 sm:px-0 sm:pt-0 bg-white rounded-t-2xl">
              <div className="flex items-center text-xl md:text-2xl font-semibold cursor-default">
                <h1 className="text-2xl">{expert?.pseudo}</h1>
                <SharedComponents.Tooltip
                  position="bottom"
                  className="w-64 z-10"
                  arrow
                  message={t("common:accessCodeTips")}
                >
                  <span className="hidden sm:block border border-slate-100 bg-slate-50 hover:bg-slate-100 text-gray-800 text-base font-semibold ml-6 px-1 py-0.5 rounded">{String(expert?.expertCode).padStart(3, "0")}</span>
                </SharedComponents.Tooltip>
              </div>
              <div className="flex sm:justify-center sm:mt-9 sm:mb-3">
                {expert?.vocalMessage && expert?.vocalMessage.length > 0 && (
                  <>
                    <button
                      type="button"
                      aria-label="Audio presentation"
                      className="px-3"
                      onClick={toggleVocalMessage}
                      data-pw="expertVocalMessage"
                    >
                      <FontAwesomeIcon icon={!vocalMessagePlaying ? faRegPlay : faRegStop} className="scale-150" />
                    </button>
                    <audio src={expert?.vocalMessage} ref={vocalMessageElementRef} className="hidden" />
                  </>
                )}

                <button
                  type="button"
                  aria-label="Favorite"
                  className="px-3"
                  onClick={isMemberFavorite
                    ? handleRemoveMemberFavorite
                    : handleAddMemberFavorite}
                  data-pw="expertFavorite"
                >
                  <FontAwesomeIcon
                    icon={faRegHeart}
                    className={`scale-150 ${isMemberFavorite ? "text-red-500" : ""}`}
                  />
                </button>

                <button
                  type="button"
                  aria-label="Notifications"
                  className="px-3"
                  data-pw="expertNotification"
                  onClick={handleAlertModal}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 493 511.92" className={`w-6 h-6 ${memberAlert ? "fill-red-500" : ""}`}>
                    <path d="M277.16 41.75c49.87 6.77 94.55 29.88 128.47 63.79 40.67 40.67 65.83 96.87 65.83 158.93 0 62.08-25.15 118.28-65.83 158.96a227.22 227.22 0 0 1-25.34 21.83l27.24 38.33c5.68 8.18 3.65 19.42-4.54 25.11-8.19 5.68-19.44 3.65-25.12-4.54l-28.28-39.78c-30.84 15.91-65.83 24.89-102.92 24.89-37.7 0-73.23-9.28-104.43-25.69l-26.59 39.71c-5.54 8.28-16.76 10.5-25.04 4.95-8.29-5.54-10.5-16.75-4.95-25.03l26.07-38.95a225.636 225.636 0 0 1-24-20.83c-40.68-40.68-65.84-96.89-65.84-158.96 0-62.07 25.16-118.26 65.84-158.94 36.44-36.43 85.34-60.39 139.74-65.03 16.45-1.4 33.38-.96 49.69 1.25zm204.53 102.98c17.3-41.28 15.24-84.52-9.51-113.49-29.7-34.77-83.39-38.75-133.26-14.3 53.01 36.36 101.12 78.78 142.77 127.79zm-470.15 1.35C-6.1 104.02-4.01 59.97 21.21 30.45 51.47-4.97 106.18-9.03 156.99 15.88c-54 37.06-103.03 80.26-145.45 130.2zm269.3 101.47 67.65-1.18c9.97-.17 18.19 7.76 18.36 17.73.18 9.97-7.76 18.19-17.73 18.37l-69.51 1.21c-6.61 11.32-18.89 18.93-32.94 18.93-21.05 0-38.12-17.08-38.12-38.13 0-14.52 8.13-27.15 20.08-33.58v-87.35c0-9.97 8.07-18.05 18.04-18.05 9.97 0 18.06 8.08 18.06 18.05v87.35a38.324 38.324 0 0 1 16.11 16.65zm99.27-116.5c-34.14-34.14-81.32-55.26-133.43-55.26-52.1 0-99.28 21.12-133.42 55.26-34.15 34.14-55.27 81.32-55.27 133.43 0 52.11 21.12 99.28 55.27 133.43 34.14 34.14 81.31 55.26 133.41 55.26 52.12 0 99.29-21.12 133.43-55.26 34.14-34.15 55.28-81.32 55.28-133.44 0-52.1-21.13-99.27-55.27-133.42z" />
                  </svg>
                </button>

                {member?.consultedExperts?.includes(expert?.expertUid) && (
                  <button
                    type="button"
                    aria-label="Consultations"
                    className="px-3"
                    onClick={goToConsultation}
                  >
                    <FontAwesomeIcon
                      icon={faPeopleArrows}
                      className="scale-150"
                    />
                  </button>
                )}

                {showMemberAlertModal && expertUid && (
                  <MemberAlertModal
                    alert={memberAlert}
                    expertIdentifier={expertIdentifiers[expertUid]}
                    onClose={() => setShowMemberAlertModal(false)}
                  />
                )}
              </div>
            </div>
            <div
              className="grid grid-cols-2 gap-1 border-slate-200 border-t text-center pt-4 md:pt-6"
            >
              <div>
                <div className="uppercase text-slate-400 text-sm">
                  {t("consultations")}
                </div>
                <div className="text-2xl text-black font-medium">
                  {expert?.totalConsultations}
                </div>
              </div>
              <Link href="#latest-reviews" className="w-fit mx-auto">
                <div className="uppercase text-slate-400 text-sm w-fit mx-auto">
                  {t("ratings")}
                </div>
                <div className="text-2xl font-medium text-black w-fit mx-auto">
                  {expert?.totalRatings}
                </div>
              </Link>

            </div>
          </div>

          <div ref={consultationRef} className="fixed md:hidden sm:sticky sm:top-60 sm:shadow z-30 bg-white bottom-0 left-0 right-0">
            <div className="flex items-center justify-center gap-6 py-6">
              <ExpertButton
                expert={expert}
                className="ring-white ring-4 p-8 sm:p-6 lg:p-8 -mt-8"
                faClassName="text-2xl sm:text-xl lg:text-2xl"
              />
            </div>

            <ExpertAvailabilitlyBadge expert={expert} defaultElement className="justify-center rounded-none" />
            <ExpertBusyDuration expert={expert} className="text-center text-sm rounded-none" />
            <ExpertQueueBadge expert={expert} className="rounded-none" />
          </div>
        </div>

        <div className="hidden md:block row-span-2 relative">
          <div className="flex flex-col md:sticky top-60 shadow-sm z-10 bg-white rounded-sm py-6 gap-6">
            <div className="flex items-center justify-center gap-6">
              <ExpertButton
                expert={expert}
                className="ring-white ring-4 lg:p-6 xl:p-8"
                faClassName="text-lg lg:text-xl xl:text-2xl"
              />
              {/* // Implementation of A/B testing feature */}
              {/* <EventTrackerWrapper
                renderAfterLoading
                identifier="ft_buttons"
                versions={[
                  { key: "A", render: <ExpertButton expert={expert} className="ring-white ring-4 p-8 bg-red-500" faClassName="text-xl" /> },
                  { key: "B", render: <ExpertButton expert={expert} className="ring-white ring-4 p-8 bg-yellow-500" faClassName="text-xl" /> },
                  { key: "C", render: <ExpertButton expert={expert} className="ring-white ring-4 p-8 bg-green-500" faClassName="text-xl" /> },
                ]}
              >
                <ExpertButton
                  expert={expert}
                  className="ring-white ring-4 p-8"
                  faClassName="text-2xl"
                />
              </EventTrackerWrapper> */}
            </div>

            <ExpertAvailabilitlyBadge expert={expert} defaultElement className="mx-auto" />
            <ExpertBusyDuration expert={expert} className="mx-auto text-center text-sm mt-2" />
            <ExpertQueueBadge expert={expert} className="mx-auto" />
          </div>
        </div>

        <div className="col-span-1 md:col-span-2 p-6 sm:px-3 font-light bg-white">
          {contentLoading && (
            <div className="text-center my-20">
              <FontAwesomeIcon
                className="text-5xl text-slate-500 w-10 mx-auto"
                icon={faCircleNotch}
                spin
              />
            </div>
          )}

          {expert?.skills && expert?.skills.length > 0 && (
            <div className="my-12">
              <div className="mb-9 text-3xl">{t("skills")}</div>
              <div>{expert?.skills.map((s) => t(`common:skills.${s}`)).join(", ")}</div>
            </div>
          )}

          <div className="my-12">
            <div className="my-9 text-3xl">{t("common:expertBio")}</div>
            {expert?.quote && expert?.quote.length > 0 && (
              <div className="font-lg border-t border-b border-slate-100 py-6 text-slate-500 italic">{expert?.quote}</div>
            )}
            {!contentLoading
              && data?.expert?.content?.description
              && data.expert?.content.description.length > 0
              && (
                <div className="mt-6 text-lg max-w-sm sm:max-w-fit overflow-hidden">
                  <TextEllipsis
                    text={data.expert?.content.description}
                    length={500}
                    className=""
                    showMore={ellipsisBtnFactory(
                      t("common:showMore"),
                      "block m-auto my-6 font-medium",
                    )}
                    showLess={ellipsisBtnFactory(
                      t("common:showLess"),
                      "block m-auto my-6 font-medium",
                    )}
                  />
                </div>
              )}
          </div>

          <div className="my-12">
            <div className="flex flex-wrap gap-x-6 gap-y-2 items-baseline my-9">
              <div className="text-3xl">{t("common:schedule")}</div>
              <div
                className=" underline underline-offset-2 decoration-dotted cursor-pointer"
                onClick={() => setShowLegend((prev) => !prev)}
              >
                {t("legend.toggle")}
              </div>

            </div>

            <div>
              <SharedComponents.SizeAwareWrapper breakpoints={{
                width: [
                  { threshold: 0, props: { nbDays: 1 } },
                  { threshold: 400, props: { nbDays: 2 } },
                  { threshold: 600, props: { nbDays: 3 } },
                  { threshold: 1000, props: { nbDays: 4 } },
                ],
              }}
              >
                {(sizeProps) => (<ExpertPlanning expert={expert} showLegend={showLegend} {...sizeProps} />)}
              </SharedComponents.SizeAwareWrapper>
            </div>
          </div>

          <div className="my-12">
            <div className="my-9 text-3xl">{t("common:contactExpert")}</div>

            <div className="my-6">
              {t("common:contactExpertDescription")}
            </div>
            <div className="">{t("common:contactExpertConditions")}</div>
            <div className="pb-6">
              <ul className="list-disc pl-6">
                <li>{t("common:contactExpertConditions1")}</li>
                <li>{t("common:contactExpertConditions2")}</li>
              </ul>
              <div className="pt-4">
                <Tag color="green" className="rounded-full mb-2">{t("common:new")}</Tag>
                <ul className="list-disc pl-6">
                  <li>{t("common:contactExpertExplanation1")}</li>
                  <li>{t("common:contactExpertExplanation2")}</li>
                  <li>{t("common:contactExpertExplanation3")}</li>
                </ul>
                <Button
                  size="medium"
                  render="link"
                  onClick={() => setShowPMExplanationModal(true)}
                  gutterless
                  className="mt-2"
                >
                  {t("common:learnMore")}
                  <FontAwesomeIcon icon={faArrowRightLong} className="ml-3" />
                </Button>
              </div>
              {typeof expertLimits?.expertLimits?.availableMessageNbr === "number" && (
                <div className="mt-6 p-4 bg-gray-100 rounded-md">
                  <Trans
                    i18nKey="expert:contactExpertLimit"
                    values={{
                      name: expert?.pseudo,
                      count: expertLimits.expertLimits?.availableMessageNbr,
                    }}
                    components={[
                      <span className="font-semibold" />,
                    ]}
                  />
                </div>
              )}
            </div>

            <div className="mb-6">
              <Button
                size="medium"
                render="primary"
                onClick={() => setModal(true)}
                loading={loading}
                data-pw="contactExpert"
              >
                <FontAwesomeIcon
                  className="fa-fw mr-3"
                  icon={faEnvelope}
                />
                {t("common:contactExpert")}
              </Button>
            </div>
          </div>
          {expert && ratingSummaryList && defaultRatings && (
            <RatingsList expert={expert} defaultRatings={defaultRatings} ratingSummaryList={ratingSummaryList} />
          )}
        </div>
      </div>

      {suggestedExperts
        && suggestedExperts.length > 0
        && (
          <div className="wrapper md:px-6 bg-white">
            <div className="p-6 py-9 text-3xl bg-white">
              {t("common:expertsSuggestions")}
              <span className="hidden">{`(${suggestedExperts.length})`}</span>
            </div>

            <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 m-3">
              {suggestedExperts.map((suggestedExpert, idx) => (
                <ExpertDetailCard
                  key={idx}
                  expert={suggestedExpert}
                  miniature
                  roundedTop
                  expertLink
                  imageOverlayTheme="light"
                  noIndexing
                />
              ))}
            </div>
          </div>
        )}

      {modal && expert && (
        <PrivateMessageModal
          expert={expert}
          onClose={() => setModal(false)}
        />
      )}

      <Modal
        title={t("common:howPrivateMessageWorks")}
        show={showPMExplanationModal}
        onClose={() => setShowPMExplanationModal(false)}
      >
        <div className="flex flex-col gap-y-4">
          <p>{t("common:howPrivateMessageWorksExplanation1")}</p>
          <p>{t("common:howPrivateMessageWorksExplanation2")}</p>
          <p>{t("common:howPrivateMessageWorksExplanation3")}</p>
          <div className="text-end">
            <Button size="small" onClick={() => setShowPMExplanationModal(false)}>{t("common:understood")}</Button>
          </div>
        </div>
      </Modal>
    </div>
  ) : (
    <>
      {(loading) && (
        <div className="text-center my-20">
          <FontAwesomeIcon
            className="text-5xl text-slate-500 w-10 mx-auto"
            icon={faCircleNotch}
            spin
          />
        </div>
      )}
      {!loading && (<PageNotFound />)}
    </>
  );
};

export default ExpertDetail;
